import React, { useState, useEffect, useRef, memo, useCallback, Component } from 'react';
import $ from 'jquery';
import DOMPurify from 'dompurify';

// Make jQuery available globally for MailChimp
window.jQuery = $;
window.$ = $;

// California Local Date Utility - Prevents timezone conversion issues
const parseCaliforniaDate = (dateString) => {
  if (!dateString) return null;
  
  // Parse date as local California date without timezone conversion
  const parts = dateString.split('-');
  if (parts.length === 3) {
    const year = parseInt(parts[0]);
    const month = parseInt(parts[1]) - 1; // JavaScript months are 0-indexed
    const day = parseInt(parts[2]);
    
    // Create date in local timezone (California)
    return new Date(year, month, day);
  }
  
  return new Date(dateString);
};

// Format date for display (California local time)
const formatEventDateForDisplay = (dateString) => {
  const date = parseCaliforniaDate(dateString);
  if (!date || isNaN(date)) return 'TBD';
  
  return date.toLocaleDateString('en-US', { 
    weekday: 'long',
    year: 'numeric',
    month: 'long', 
    day: 'numeric'
  });
};

// Format date for simple display (California local time)
const formatEventDateSimple = (dateString) => {
  const date = parseCaliforniaDate(dateString);
  if (!date || isNaN(date)) return 'TBD';
  
  return date.toLocaleDateString('en-US');
};

// Event Category Color Utility
const getEventCategoryStyle = (category) => {
  const styles = {
    'Concert': 'font-bold text-blue-500',
    'Comedy': 'font-bold text-purple-500', 
    'Theater': 'font-bold text-green-500',
    'Special Event': 'font-bold',
    'Movie': 'font-bold text-red-500',
    'Movies': 'font-bold text-red-500'
  };
  
  return styles[category] || 'font-bold text-gray-600';
};

// Get calendar event colors based on category
const getCalendarEventStyle = (category) => {
  const styles = {
    'Concert': 'bg-blue-500 hover:bg-blue-600',
    'Comedy': 'bg-purple-500 hover:bg-purple-600', 
    'Theater': 'bg-green-500 hover:bg-green-600',
    'Special Event': 'bg-orange-500 hover:bg-orange-600',
    'Movie': 'bg-red-500 hover:bg-red-600',
    'Movies': 'bg-red-500 hover:bg-red-600'
  };
  
  return styles[category] || 'bg-gray-500 hover:bg-gray-600';
};

// Header Component
const Header = ({ mobileMenuOpen, setMobileMenuOpen, theaterName, contacts, currentView, setCurrentView }) => {
  // Safe guard for contacts data during loading
  const safeContacts = contacts || {
    phone: '',
    socialMedia: {
      facebook: '#',
      twitter: '#',
      tiktok: '#',
      instagram: '#',
      spotify: '#'
    }
  };
  const menuItems = [
    { name: 'HOME', action: () => {
        setCurrentView('home');
        setTimeout(() => window.scrollTo(0, 0), 100);
      }
    },
    { name: 'EVENTS', action: () => {
        setCurrentView('home');
        // Scroll to upcoming events section after a brief delay, with offset for title visibility
        setTimeout(() => {
          const eventsSection = document.getElementById('upcoming-events');
          if (eventsSection) {
            const yOffset = -100; // Offset to ensure title is visible
            const y = eventsSection.getBoundingClientRect().top + window.pageYOffset + yOffset;
            window.scrollTo({ top: y, behavior: 'smooth' });
          }
        }, 100);
      }
    },
    { name: 'CALENDAR', action: () => {
        setCurrentView('calendar');
        setTimeout(() => {
          window.scrollTo(0, 0);
          // Trigger auto-scroll to current date after calendar loads
          setTimeout(() => {
            const today = new Date();
            const currentDay = today.getDate();
            const currentDayElement = document.querySelector(`[data-day="${currentDay}"]`);
            if (currentDayElement) {
              currentDayElement.scrollIntoView({ 
                behavior: 'smooth', 
                block: 'center' 
              });
            }
          }, 500);
        }, 100);
      }
    },
    { name: 'GALLERY', action: () => {
        setCurrentView('gallery');
        setTimeout(() => window.scrollTo(0, 0), 100);
      }
    },
    { name: 'ABOUT', action: () => {
        setCurrentView('about');
        setTimeout(() => window.scrollTo(0, 0), 100);
      }
    },
    { name: 'FAQ', action: () => {
        setCurrentView('faq');
        setTimeout(() => window.scrollTo(0, 0), 100);
      }
    },
    { name: 'CONTACT US', action: () => {
        setCurrentView('home');
        // Scroll to footer/contact section after a brief delay
        setTimeout(() => {
          const footer = document.querySelector('footer');
          if (footer) {
            footer.scrollIntoView({ behavior: 'smooth' });
          }
        }, 100);
      }
    },
    { name: 'EMPRESS TAVERN', action: () => {
        setCurrentView('empress');
        setTimeout(() => window.scrollTo(0, 0), 100);
      }
    }
  ];

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-white shadow-lg">
      {/* Top bar with phone and social */}
      <div className="bg-gray-100 py-2">
        <div className="container mx-auto px-4 flex justify-between items-center">
          <div></div>
          <div className="flex items-center space-x-4">
            <span className="text-sm font-semibold text-gray-700">
              CALL NOW: <a href={`tel:${safeContacts.phone}`} className="text-gray-700 hover:text-gray-900 transition-colors underline">{safeContacts.phone}</a>
            </span>
            <div className="flex space-x-2">
              <a href={safeContacts.socialMedia.facebook} target="_blank" rel="noopener noreferrer" className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center text-white text-xs hover:bg-blue-700 transition-colors" title="Facebook">
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                </svg>
              </a>
              <a href={safeContacts.socialMedia.twitter} target="_blank" rel="noopener noreferrer" className="w-8 h-8 bg-black rounded-full flex items-center justify-center text-white text-xs hover:bg-gray-800 transition-colors" title="X (Twitter)">
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                </svg>
              </a>
              <a href={safeContacts.socialMedia.tiktok} target="_blank" rel="noopener noreferrer" className="w-8 h-8 bg-black rounded-full flex items-center justify-center text-white text-xs hover:bg-gray-800 transition-colors" title="TikTok">
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M19.321 5.562a5.124 5.124 0 0 1-.443-.258 6.228 6.228 0 0 1-1.137-.966c-.849-.849-1.086-1.673-1.086-2.264V2h-3.006v14.63c0 2.259-1.832 4.091-4.091 4.091s-4.091-1.832-4.091-4.091 1.832-4.091 4.091-4.091c.446 0 .875.072 1.28.204v-3.13c-.424-.058-.857-.087-1.28-.087C4.775 9.526 0 14.301 0 20.185 0 26.069 4.775 30.844 10.659 30.844c5.884 0 10.659-4.775 10.659-10.659V9.697c1.007.6 2.188.952 3.435.952v-3.006c-1.932 0-3.623-.932-4.432-2.081z"/>
                </svg>
              </a>
              <a href={safeContacts.socialMedia.instagram} target="_blank" rel="noopener noreferrer" className="w-8 h-8 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full flex items-center justify-center text-white text-xs hover:from-purple-600 hover:to-pink-600 transition-colors" title="Instagram">
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12.017 0C8.396 0 7.952.01 6.729.05 5.507.09 4.668.256 3.932.488 3.162.73 2.499 1.078 1.86 1.717.7 2.877.352 3.552.093 4.322c-.224.766-.39 1.605-.43 2.827C-.01 7.952 0 8.396 0 12.017c0 3.622.01 4.066.05 5.289.04 1.222.206 2.061.438 2.827.258.77.606 1.435 1.245 2.074.639.639 1.304.987 2.074 1.245.766.232 1.605.398 2.827.438 1.223.04 1.667.05 5.289.05 3.622 0 4.066-.01 5.289-.05 1.222-.04 2.061-.206 2.827-.438.77-.258 1.435-.606 2.074-1.245.639-.639.987-1.304 1.245-2.074.232-.766.398-1.605.438-2.827.04-1.223.05-1.667.05-5.289 0-3.622-.01-4.066-.05-5.289-.04-1.222-.206-2.061-.438-2.827-.258-.77-.606-1.435-1.245-2.074C20.491 1.078 19.826.73 19.056.488 18.29.256 17.451.09 16.229.05 15.006.01 14.562 0 10.941 0H12.017zM12.017 2.163c3.556 0 3.974.014 5.382.052 1.298.059 2.004.277 2.474.46.622.242 1.067.531 1.534.998.467.467.756.912.998 1.534.183.47.401 1.176.46 2.474.038 1.408.052 1.826.052 5.382 0 3.556-.014 3.974-.052 5.382-.059 1.298-.277 2.004-.46 2.474-.242.622-.531 1.067-.998 1.534-.467.467-.912.756-1.534.998-.47.183-1.176.401-2.474.46-1.408.038-1.826.052-5.382.052-3.556 0-3.974-.014-5.382-.052-1.298-.059-2.004-.277-2.474-.46-.622-.242-1.067-.531-1.534-.998-.467-.467-.756-.912-.998-1.534-.183-.47-.401-1.176-.46-2.474-.038-1.408-.052-1.826-.052-5.382 0-3.556.014-3.974.052-5.382.059-1.298.277-2.004.46-2.474.242-.622.531-1.067.998-1.534.467-.467.912-.756 1.534-.998.47-.183 1.176-.401 2.474-.46 1.408-.038 1.826-.052 5.382-.052z"/>
                  <circle cx="12.017" cy="12.017" r="3.708"/>
                  <circle cx="18.408" cy="5.622" r="1.178"/>
                </svg>
              </a>
              <a href={safeContacts.socialMedia.spotify} target="_blank" rel="noopener noreferrer" className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center text-white text-xs hover:bg-green-600 transition-colors" title="Spotify">
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0zm5.521 17.34c-.24.359-.66.48-1.021.24-2.82-1.74-6.36-2.101-10.561-1.141-.418.122-.779-.179-.899-.539-.12-.421.18-.78.54-.9 4.56-1.021 8.52-.6 11.64 1.32.42.18.479.659.301 1.02zm1.44-3.3c-.301.42-.841.6-1.262.3-3.239-1.98-8.159-2.58-11.939-1.38-.479.12-1.02-.12-1.14-.6-.12-.48.12-1.021.6-1.141C9.6 9.9 15 10.561 18.72 12.84c.361.181.54.78.241 1.2zm.12-3.36C15.24 8.4 8.82 8.16 5.16 9.301c-.6.179-1.2-.181-1.38-.721-.18-.601.18-1.2.72-1.381 4.26-1.26 11.28-1.02 15.721 1.621.539.3.719 1.02.42 1.56-.299.421-1.02.599-1.559.3z"/>
                </svg>
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Main header */}
      <div className="bg-white">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          {/* Logo */}
          <div className="flex items-center cursor-pointer" onClick={() => setCurrentView('home')}>
            <div className="text-3xl md:text-4xl font-bold">
              <span className="text-red-600 italic font-script">The Crest</span>
              <span className="text-yellow-600 ml-2 font-serif">THEATER</span>
            </div>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex rounded-lg" style={{ backgroundColor: '#E9A319' }}>
            {menuItems.map((item, index) => (
              <button
                key={index}
                onClick={item.action}
                className={`px-4 py-3 text-white font-semibold text-sm hover:opacity-80 transition-colors first:rounded-l-lg ${
                  index === menuItems.length - 1 ? 'rounded-r-lg' : ''
                } ${
                  (item.name === 'HOME' && currentView === 'home') || 
                  (item.name === 'CALENDAR' && currentView === 'calendar') ||
                  (item.name === 'GALLERY' && currentView === 'gallery') ||
                  (item.name === 'ABOUT' && currentView === 'about') ||
                  (item.name === 'FAQ' && currentView === 'faq') ||
                  (item.name === 'EMPRESS TAVERN' && currentView === 'empress')
                    ? 'opacity-80' : ''
                }`}
              >
                {item.name}
              </button>
            ))}
          </nav>

          {/* Mobile menu button */}
          <button
            className="lg:hidden flex flex-col space-y-1"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            <span className="w-6 h-0.5 bg-gray-600"></span>
            <span className="w-6 h-0.5 bg-gray-600"></span>
            <span className="w-6 h-0.5 bg-gray-600"></span>
          </button>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <div className="lg:hidden" style={{ backgroundColor: '#E9A319' }}>
            {menuItems.map((item, index) => (
              <button
                key={index}
                onClick={() => {
                  item.action();
                  setMobileMenuOpen(false);
                }}
                className="block w-full text-left px-4 py-3 text-white font-semibold border-b hover:opacity-80 transition-colors"
                style={{ borderColor: 'rgba(255,255,255,0.2)' }}
              >
                {item.name}
              </button>
            ))}
          </div>
        )}
      </div>
    </header>
  );
};

// Hero Section Component
const HeroSection = ({ theaterName, subtitle, description, pageImages, bannerSettings }) => {
  const heroBackgroundImage = pageImages?.hero || "https://i.imgur.com/vi3Cqu1.jpeg";

  // Function to check if banner text is truly empty (handles HTML content)
  const isBannerTextEmpty = (text) => {
    if (!text) return true;
    
    // Create a temporary div to parse HTML and get text content
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = text;
    const textContent = tempDiv.textContent || tempDiv.innerText || '';
    
    // Check if text content is empty after trimming
    return textContent.trim() === '';
  };

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
      {/* Background Image */}
      <div
        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage: `url(${heroBackgroundImage})`,
          filter: 'brightness(0.8)'
        }}
      ></div>

      {/* Overlay */}
      <div className="absolute inset-0 bg-gradient-to-b from-black/40 to-black/60"></div>

      {/* Content - Fluid scaling with increased top spacing */}
      <div className="relative z-10 text-center text-white px-4 pt-20 md:pt-28 lg:pt-32 xl:pt-36 pb-6">
        <div className="mb-8">
          <h1 
            className="font-bold leading-tight mb-8" 
            style={{ 
              fontSize: 'clamp(2.5rem, 8vw, 6rem)',
              textShadow: '2px 2px 4px rgba(0,0,0,0.8)' 
            }}
          >
            <span className="text-red-600 italic font-script block sm:inline">The Crest</span>
            <span className="text-white block sm:inline"> THEATER</span>
          </h1>
          <div className="max-w-4xl mx-auto px-2">
            <p 
              className="text-gray-200 leading-relaxed text-center mb-4" 
              style={{ 
                fontSize: 'clamp(1.125rem, 2.5vw, 1.875rem)',
                textShadow: '2px 2px 4px rgba(0,0,0,0.8)' 
              }}
            >
              Experience the Legend. Discover Sacramento's Premier Entertainment Destination.
            </p>
            <p 
              className="text-gray-300 leading-relaxed text-center" 
              style={{ 
                fontSize: 'clamp(1rem, 2vw, 1.5rem)',
                textShadow: '2px 2px 4px rgba(0,0,0,0.8)' 
              }}
            >
              From electrifying concerts and captivating films to unforgettable private events,
              The Crest Theater offers a timeless experience in the heart of downtown.
            </p>
          </div>
        </div>

        {/* Banner - Below text, only show if text content is truly not empty */}
        {bannerSettings?.text && !isBannerTextEmpty(bannerSettings.text) && (
          <BannerWithDynamicStars bannerSettings={bannerSettings} />
        )}
      </div>
    </section>
  );
};

// Newsletter Signup Component with MailChimp Integration
const NewsletterSignup = memo(() => {
  useEffect(() => {
    // Ensure jQuery is available
    if (typeof window.jQuery === 'undefined') {
      console.error('jQuery is not available');
      return;
    }

    // Load MailChimp CSS
    const link = document.createElement('link');
    link.href = '//cdn-images.mailchimp.com/embedcode/classic-061523.css';
    link.rel = 'stylesheet';
    link.type = 'text/css';
    document.head.appendChild(link);

    // Load MailChimp JavaScript with proper error handling
    const script1 = document.createElement('script');
    script1.src = '//s3.amazonaws.com/downloads.mailchimp.com/js/mc-validate.js';
    script1.type = 'text/javascript';
    script1.onload = () => {
      // Initialize MailChimp validation after script loads
      if (window.jQuery) {
        const script2 = document.createElement('script');
        script2.type = 'text/javascript';
        script2.innerHTML = `
          (function($) {
            window.fnames = new Array(); 
            window.ftypes = new Array();
            fnames[0]='EMAIL';ftypes[0]='email';
            fnames[1]='FNAME';ftypes[1]='text';
            fnames[2]='LNAME';ftypes[2]='text';
            fnames[3]='ADDRESS';ftypes[3]='address';
            fnames[4]='PHONE';ftypes[4]='phone';
            fnames[5]='BIRTHDAY';ftypes[5]='birthday';
            fnames[6]='COMPANY';ftypes[6]='text';
          }(jQuery));
          var $mcj = jQuery.noConflict(true);
        `;
        document.body.appendChild(script2);
      }
    };
    script1.onerror = () => {
      console.warn('MailChimp validation script failed to load');
    };
    document.body.appendChild(script1);

    return () => {
      // Cleanup on unmount
      try {
        if (link.parentNode) document.head.removeChild(link);
        if (script1.parentNode) document.body.removeChild(script1);
      } catch (e) {
        console.warn('Cleanup error:', e);
      }
    };
  }, []);

  return (
    <section className="py-8" style={{ backgroundColor: '#E9A319' }}>
      <div className="container mx-auto px-4">
        <div className="max-w-xl mx-auto text-center text-white">
          <h2 className="text-2xl font-bold mb-2">STAY CONNECTED</h2>
          <h3 className="text-lg mb-6">Sign up for our newsletter to get the latest updates on shows and events</h3>
          
          {/* MailChimp Integration */}
          <div id="mc_embed_shell">
            <style type="text/css">
              {`
                #mc_embed_signup {
                  background: transparent;
                  clear: left;
                  font: 14px Helvetica, Arial, sans-serif;
                  width: 100%;
                  max-width: 500px;
                  margin: 0 auto;
                }
                #mc_embed_signup form {
                  text-align: center;
                  padding: 0;
                }
                #mc_embed_signup h2 {
                  display: none;
                }
                #mc_embed_signup .indicates-required {
                  color: white;
                  font-size: 11px;
                  margin: 0 0 8px 0;
                }
                #mc_embed_signup .mc-field-group {
                  margin-bottom: 12px;
                }
                #mc_embed_signup .mc-field-group label {
                  display: none;
                }
                #mc_embed_signup input[type="email"] {
                  padding: 10px;
                  border: none;
                  border-radius: 4px;
                  width: 250px;
                  max-width: 100%;
                  font-size: 14px;
                  margin-right: 8px;
                  color: #333;
                  background-color: white;
                }
                #mc_embed_signup input[type="email"]:focus {
                  outline: 2px solid #dc2626;
                  background-color: white;
                }
                #mc_embed_signup input[type="submit"] {
                  background-color: #dc2626;
                  color: white;
                  padding: 10px 20px;
                  border: none;
                  border-radius: 4px;
                  font-weight: bold;
                  font-size: 14px;
                  cursor: pointer;
                  transition: background-color 0.3s;
                  text-align: center;
                  display: inline-flex;
                  align-items: center;
                  justify-content: center;
                  min-width: 100px;
                  line-height: 1;
                }
                #mc_embed_signup input[type="submit"]:hover {
                  background-color: #b91c1c;
                }
                #mc_embed_signup .optionalParent {
                  margin-top: 12px;
                }
                #mc_embed_signup .optionalParent p {
                  margin: 8px 0 0 0;
                }
                #mc_embed_signup .response {
                  color: white;
                  font-weight: bold;
                  margin: 8px 0;
                }
                @media (max-width: 640px) {
                  #mc_embed_signup input[type="email"] {
                    width: 100%;
                    margin-right: 0;
                    margin-bottom: 8px;
                  }
                  #mc_embed_signup input[type="submit"] {
                    width: 100%;
                  }
                }
              `}
            </style>
            <div id="mc_embed_signup">
              <form 
                action="https://cresttheater.us1.list-manage.com/subscribe/post?u=1da447d00b14762f22a73cf8b&amp;id=9953bcf864&amp;f_id=0054c3e0f0" 
                method="post" 
                id="mc-embedded-subscribe-form" 
                name="mc-embedded-subscribe-form" 
                className="validate" 
                target="_blank"
              >
                <div id="mc_embed_signup_scroll">
                  <h2>Subscribe</h2>
                  <div className="indicates-required">
                    <span className="asterisk">*</span> indicates required
                  </div>
                  <div className="mc-field-group">
                    <label htmlFor="mce-EMAIL">Email Address <span className="asterisk">*</span></label>
                    <input 
                      type="email" 
                      name="EMAIL" 
                      className="required email" 
                      id="mce-EMAIL" 
                      required 
                      placeholder="Enter your email address"
                    />
                  </div>
                  <div id="mce-responses" className="clear foot">
                    <div className="response" id="mce-error-response" style={{ display: 'none' }}></div>
                    <div className="response" id="mce-success-response" style={{ display: 'none' }}></div>
                  </div>
                  <div aria-hidden="true" style={{ position: 'absolute', left: '-5000px' }}>
                    <input 
                      type="text" 
                      name="b_1da447d00b14762f22a73cf8b_9953bcf864" 
                      tabIndex="-1" 
                    />
                  </div>
                  <div className="optionalParent">
                    <div className="clear foot">
                      <input 
                        type="submit" 
                        name="subscribe" 
                        id="mc-embedded-subscribe" 
                        className="button" 
                        value="SUBSCRIBE"
                      />
                      <p style={{ margin: '0px auto' }}>
                        <a 
                          href="http://eepurl.com/jg-ja6" 
                          title="Mailchimp - email marketing made easy and fun"
                        >
                          <span style={{ display: 'inline-block', backgroundColor: 'transparent', borderRadius: '4px' }}>
                            <img 
                              className="refferal_badge" 
                              src="https://digitalasset.intuit.com/render/content/dam/intuit/mc-fe/en_us/images/intuit-mc-rewards-text-dark.svg" 
                              alt="Intuit Mailchimp" 
                              style={{ width: '180px', height: '32px', display: 'flex', padding: '2px 0px', justifyContent: 'center', alignItems: 'center' }}
                            />
                          </span>
                        </a>
                      </p>
                    </div>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
});

// Rental Information Component - Updated with new image
const RentalInformation = memo(({ pageImages }) => {
  return (
    <section className="py-8 bg-gradient-to-r from-red-900 to-red-800">
      <div className="container mx-auto px-4">
        <div className="max-w-6xl mx-auto text-white">
          <h2 className="text-2xl font-bold mb-6 text-center">VENUE RENTAL and BOOKING</h2>
          <div className="grid md:grid-cols-2 gap-8 items-center">
            <div>
              <p className="text-sm mb-4">
                Available for day as well as evening rentals, The Crest Theater accommodates a wide variety of events including:
              </p>
              <ul className="list-disc list-inside space-y-1 mb-6 text-sm">
                <li>Corporate events and meetings</li>
                <li>Private parties and celebrations</li>
                <li>Wedding receptions</li>
                <li>Film screenings and premieres</li>
                <li>Musical performances and concerts</li>
              </ul>
              <a 
                href="mailto:booking@cresttheater.com?subject=Request%20Rental/Booking%20Information&body=I%20am%20interested%20in%20rental%20and%20booking%20information%20about%20the%20Crest%20Theater." 
                className="bg-white text-red-900 px-6 py-3 rounded font-bold hover:bg-gray-100 transition-colors inline-block text-sm"
              >
                Request Rental/Booking Information
              </a>
            </div>
            <div className="text-center">
              <div className="mb-4">
                <img 
                  src={pageImages?.rental || "https://i.imgur.com/b1k6F4U.jpeg"} 
                  alt="Venue Rental Interior" 
                  className="w-full h-48 object-cover rounded-lg shadow-lg"
                  loading="lazy"
                />
              </div>
              <h3 className="text-lg font-bold mb-2">Historic Venue</h3>
              <p className="text-sm">Experience the elegance and charm of Sacramento's premier historic theater for your special event.</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
});

// Event Detail Component
const EventDetail = ({ event, onClose }) => {
  if (!event) return null;

  const formatEventDate = (dateString) => {
    return formatEventDateForDisplay(dateString);
  };

  const getTicketUrl = (event) => {
    // If custom ticket link is provided, use it
    if (event.ticketLink && event.ticketLink.trim()) {
      return event.ticketLink;
    }
    
    // Generate default URLs based on ticketing agency
    const eventTitle = encodeURIComponent(`${event.title} Crest Theater Sacramento`);
    
    switch (event.ticketingAgency) {
      case 'Eventbrite':
        return `https://www.eventbrite.com/d/ca--sacramento/${encodeURIComponent(event.title)}/`;
      case 'Other':
        // Should not reach here if validation works, but fallback to Ticketmaster
        return `https://www.ticketmaster.com/search?q=${eventTitle}`;
      case 'Ticketmaster':
      default:
        return `https://www.ticketmaster.com/search?q=${eventTitle}`;
    }
  };

  const getTicketmasterUrl = (eventTitle) => {
    // Keep for backward compatibility but not used anymore
    const searchQuery = encodeURIComponent(`${eventTitle} Crest Theater Sacramento`);
    return `https://www.ticketmaster.com/search?q=${searchQuery}`;
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="relative">
          <img
            src={event.image}
            alt={event.title}
            className="w-full h-64 md:h-96 object-cover rounded-t-lg"
          />
          <button
            onClick={onClose}
            className="absolute top-4 right-4 bg-black bg-opacity-50 text-white rounded-full w-10 h-10 flex items-center justify-center hover:bg-opacity-75 transition-colors text-xl"
          >
            ×
          </button>
          <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black to-transparent p-6">
            <h1 className="text-3xl md:text-4xl font-bold text-white mb-2">{event.title}</h1>
            <p 
              className={`text-xl ${getEventCategoryStyle(event.category)}`} 
              style={{ 
                textShadow: '2px 2px 4px rgba(0,0,0,0.8)',
                color: event.category === 'Special Event' ? '#E9A319' : undefined
              }}
            >
              {event.category}
            </p>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 md:p-8">
          <div className="grid md:grid-cols-2 gap-8">
            {/* Event Details */}
            <div>
              <h2 className="text-2xl font-bold mb-4 text-gray-800">Event Details</h2>
              <div className="space-y-3">
                <div className="flex items-center">
                  <span className="font-semibold text-gray-700 w-20">Date:</span>
                  <span className="text-gray-900">{formatEventDate(event.date)}</span>
                </div>
                <div className="flex items-center">
                  <span className="font-semibold text-gray-700 w-20">Time:</span>
                  <span className="text-gray-900">{event.time || '7:00 PM'}</span>
                </div>
                <div className="flex items-center">
                  <span className="font-semibold text-gray-700 w-20">Venue:</span>
                  <span className="text-gray-900">The Crest Theater</span>
                </div>
                <div className="flex items-center">
                  <span className="font-semibold text-gray-700 w-20">Price:</span>
                  <span className="text-red-600 font-bold text-lg">{event.ticketPrice || 'TBD'}</span>
                </div>
              </div>

              {/* Ticket Purchase */}
              <div className="mt-8">
                <a
                  href={getTicketUrl(event)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-blue-600 text-white px-8 py-4 rounded-lg font-bold text-lg hover:bg-blue-700 transition-colors inline-flex items-center"
                >
                  🎫 Buy Tickets
                </a>
              </div>
            </div>

            {/* Event Bio/Description */}
            <div>
              <h2 className="text-2xl font-bold mb-4 text-gray-800">About This Event</h2>
              <div className="prose text-gray-700">
                <p className="text-lg leading-relaxed mb-4">
                  {event.description || `Join us for an unforgettable evening with ${event.title} at The Crest Theater. This ${event.category.toLowerCase()} event promises to be an extraordinary experience in Sacramento's most historic venue.`}
                </p>
                
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="font-semibold text-gray-800 mb-2">Venue Information</h3>
                  <p className="text-sm text-gray-600">
                    The Crest Theater is a historic venue located in downtown Sacramento, 
                    offering an intimate setting with excellent acoustics and sightlines. 
                    The theater has been entertaining audiences since 1949 and continues 
                    to be Sacramento's premier destination for live entertainment.
                  </p>
                </div>

                <div className="mt-4 text-sm text-gray-600">
                  <p><strong>Address:</strong> 1013 K Street, Sacramento, CA 95814</p>
                  <p><strong>Parking:</strong> Street parking and nearby garages available</p>
                  <p><strong>Accessibility:</strong> Wheelchair accessible venue</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
// Featured Event Component - Updated with functional buttons
const FeaturedEvent = ({ event, onEventClick }) => {
  const getTicketmasterUrl = (eventTitle) => {
    const searchQuery = encodeURIComponent(`${eventTitle} Crest Theater Sacramento`);
    return `https://www.ticketmaster.com/search?q=${searchQuery}`;
  };

  // Get the ticket URL - use ticketLink if provided, otherwise fallback to search
  const getTicketUrl = (event) => {
    if (event.ticketLink && event.ticketLink.trim() !== '') {
      return event.ticketLink;
    }
    return getTicketmasterUrl(event.title);
  };

  return (
    <section className="py-12 bg-gradient-to-r from-red-900 to-red-800">
      <div className="container mx-auto px-4">
        <div className="flex flex-col lg:flex-row items-center">
          <div className="lg:w-1/2 mb-6 lg:mb-0">
            <img
              src={event.image}
              alt={event.title}
              className="w-full h-72 object-cover rounded-lg shadow-2xl"
            />
          </div>
          
          <div className="lg:w-1/2 lg:pl-10 text-white">
            <h2 className="text-4xl font-bold mb-3 text-yellow-400">{event.title}</h2>
            <h3 className="text-2xl font-semibold mb-4">{event.subtitle}</h3>
            <p className="text-lg mb-4">{event.description}</p>
            
            <div className="space-y-1 mb-6 text-sm">
              <p><strong>Date:</strong> {formatEventDateForDisplay(event.date)}</p>
              <p><strong>Time:</strong> {event.time}</p>
              <p><strong>Venue:</strong> The Crest Theater</p>
              <p><strong>Price:</strong> <span className="text-yellow-400 font-bold">{event.ticketPrice}</span></p>
              <p><strong>{event.ticketInfo}</strong></p>
              <p>{event.presale}</p>
              <p>{event.onSale}</p>
            </div>
            
            <div className="space-x-3">
              <button 
                onClick={() => onEventClick && onEventClick(event)}
                className="bg-yellow-600 text-white px-6 py-2 rounded font-bold hover:bg-yellow-700 transition-colors"
              >
                MORE INFO
              </button>
              <a
                href={getTicketUrl(event)}
                target="_blank"
                rel="noopener noreferrer"
                className="bg-green-600 text-white px-6 py-2 rounded font-bold hover:bg-green-700 transition-colors inline-block"
              >
                GET TICKETS
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Upcoming Events Component
const UpcomingEvents = ({ events, onEventClick, galleryImages, showAllEvents = false, onShowMoreEvents }) => {
  // State for category filter
  const [categoryFilter, setCategoryFilter] = useState('ALL EVENTS');
  
  // Get a random background image from gallery
  const getRandomBackgroundImage = () => {
    if (galleryImages && galleryImages.length > 0) {
      const randomIndex = Math.floor(Math.random() * galleryImages.length);
      return galleryImages[randomIndex];
    }
    return '';
  };

  const backgroundImage = getRandomBackgroundImage();

  // Filter out past events (only show upcoming events)
  const filterUpcomingEvents = (events) => {
    // Get current date in PST/PDT timezone
    const now = new Date();
    const pstOffset = -8 * 60; // PST is UTC-8
    const pdtOffset = -7 * 60; // PDT is UTC-7
    
    // Simple check for daylight saving time (March-November)
    const currentMonth = now.getMonth();
    const isDST = currentMonth >= 2 && currentMonth <= 10; // March (2) to November (10)
    const timezoneOffset = isDST ? pdtOffset : pstOffset;
    
    // Get current time in California timezone
    const utcTime = now.getTime() + (now.getTimezoneOffset() * 60000);
    const californiaNow = new Date(utcTime + (timezoneOffset * 60000));
    
    // Get California date at start of current day (00:00:00)
    const currentCaliDate = new Date(californiaNow.getFullYear(), californiaNow.getMonth(), californiaNow.getDate());
    
    return events.filter(event => {
      const eventDate = parseCaliforniaDate(event.date);
      if (!eventDate) return false;
      
      // Keep events visible until the day after the event date at 12:00am PST
      // This means an event on Oct 3rd will be visible until Oct 4th at 00:00:00
      const dayAfterEvent = new Date(eventDate.getFullYear(), eventDate.getMonth(), eventDate.getDate() + 1);
      
      return currentCaliDate < dayAfterEvent;
    });
  };

  // Filter events based on selected category
  const filterEventsByCategory = (events) => {
    switch (categoryFilter) {
      case 'MOVIES':
        return events.filter(event => event.category === 'Movie');
      case 'CONCERTS, COMEDY, THEATER, and SPECIAL EVENTS':
        return events.filter(event => 
          event.category === 'Concert' || 
          event.category === 'Comedy' || 
          event.category === 'Theater' || 
          event.category === 'Special Event'
        );
      case 'ALL EVENTS':
      default:
        return events;
    }
  };

  // First filter out past events, then sort chronologically
  const upcomingEvents = filterUpcomingEvents(events);
  const sortedEvents = upcomingEvents.sort((a, b) => parseCaliforniaDate(a.date) - parseCaliforniaDate(b.date));
  
  // Apply category filter, then limit display
  const filteredEvents = filterEventsByCategory(sortedEvents);
  const displayEvents = showAllEvents ? filteredEvents : filteredEvents.slice(0, 6);

  return (
    <section id="upcoming-events" className="relative py-16 overflow-hidden">
      {/* Background Image */}
      {backgroundImage && (
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: `url(${backgroundImage})`,
            filter: 'grayscale(80%) brightness(0.3)',
            opacity: 0.4
          }}
        ></div>
      )}
      
      {/* Overlay */}
      <div className="absolute inset-0 bg-gradient-to-b from-gray-900/80 to-gray-800/90"></div>
      
      {/* Content */}
      <div className="relative z-10 container mx-auto px-4">
        <h2 className="text-4xl font-bold text-center mb-8 text-white">
          {showAllEvents ? 'ALL UPCOMING EVENTS' : 'UPCOMING EVENTS'}
        </h2>
        
        {/* Category Filter Dropdown */}
        <div className="flex justify-center mb-8">
          <div className="relative">
            <select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              className="appearance-none bg-white/95 backdrop-blur-sm border border-gray-300 rounded-lg px-6 py-3 pr-10 font-medium text-gray-800 shadow-lg hover:bg-white transition-colors cursor-pointer focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500"
            >
              <option value="ALL EVENTS">ALL EVENTS</option>
              <option value="MOVIES">MOVIES</option>
              <option value="CONCERTS, COMEDY, THEATER, and SPECIAL EVENTS">CONCERTS, COMEDY, THEATER, and SPECIAL EVENTS</option>
            </select>
            <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-gray-700">
              <svg className="fill-current h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
                <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z"/>
              </svg>
            </div>
          </div>
        </div>
        
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {displayEvents.length > 0 ? (
            displayEvents.map((event) => (
              <div key={event.id} className="bg-white/95 backdrop-blur-sm rounded-lg shadow-xl overflow-hidden hover:shadow-2xl transition-shadow cursor-pointer transform hover:-translate-y-1 transition-transform"
                   onClick={() => onEventClick && onEventClick(event)}>
                <div className="relative">
                  <img
                    src={event.image}
                    alt={event.title}
                    className="w-full h-64 object-cover"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-black bg-opacity-40 flex items-end">
                    <div className="p-4 text-white">
                      <h3 className="text-xl font-bold mb-2">{event.title}</h3>
                      <p className="text-sm">{formatEventDateSimple(event.date)} at {event.time || '7:00 PM'}</p>
                    </div>
                  </div>
                </div>
                
                <div className="p-6">
                  <div className="flex justify-between items-center mb-4">
                    <span className="text-red-600 font-bold text-lg">{event.ticketPrice || 'TBD'}</span>
                    <span 
                      className={`px-3 py-1 rounded-full text-sm ${getEventCategoryStyle(event.category)}`}
                      style={{ 
                        color: event.category === 'Special Event' ? '#E9A319' : undefined
                      }}
                    >
                      {event.category}
                    </span>
                  </div>
                  <button 
                    onClick={(e) => {
                      e.stopPropagation();
                      onEventClick && onEventClick(event);
                    }}
                    className="w-full bg-red-600 text-white py-3 rounded font-semibold hover:bg-red-700 transition-colors"
                  >
                    View Details & Buy Tickets »
                  </button>
                </div>
              </div>
            ))
          ) : (
            <div className="col-span-full text-center py-12">
              <div className="bg-white/95 backdrop-blur-sm rounded-lg p-8 mx-auto max-w-md">
                <p className="text-gray-600 text-lg">
                  No events found for "{categoryFilter}".
                </p>
                <p className="text-gray-500 text-sm mt-2">
                  Try selecting a different category or check back later for new events.
                </p>
              </div>
            </div>
          )}
        </div>
        
        {!showAllEvents && filteredEvents.length > 6 && (
          <div className="text-center mt-12">
            <button 
              onClick={onShowMoreEvents}
              className="bg-red-600 text-white px-8 py-3 rounded font-bold hover:bg-red-700 transition-colors"
            >
              SEE MORE EVENTS → ({filteredEvents.length - 6} more)
            </button>
          </div>
        )}

        {showAllEvents && (
          <div className="text-center mt-12">
            <button 
              onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
              className="bg-gray-600 text-white px-8 py-3 rounded font-bold hover:bg-gray-700 transition-colors"
            >
              ↑ Back to Top
            </button>
          </div>
        )}
      </div>
    </section>
  );
};

// About Section Component
const AboutSection = ({ theaterName }) => {
  const specificBackgroundImage = "https://i.imgur.com/VAer4Li.jpeg";
  
  return (
    <section className="relative py-32 min-h-screen overflow-hidden">
      {/* Background Image */}
      <div 
        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage: `url(${specificBackgroundImage})`,
          filter: 'brightness(0.8)',
          opacity: 1
        }}
      ></div>
      
      {/* Overlay */}
      <div className="absolute inset-0 bg-gradient-to-b from-black/40 to-black/60"></div>
      
      {/* Content */}
      <div className="relative z-10 container mx-auto px-4">
        <div className="text-center mb-12">
          <h1 className="text-5xl font-bold mb-4" style={{ textShadow: '2px 2px 4px rgba(0,0,0,0.8)' }}>
            <span className="text-red-600 italic font-script">The Crest</span>
            <span className="text-white"> THEATER</span>
          </h1>
          <h2 className="text-3xl font-bold text-white" style={{ textShadow: '2px 2px 4px rgba(0,0,0,0.8)' }}>ABOUT</h2>
        </div>

        <div className="max-w-4xl mx-auto">
          <div className="bg-white/95 backdrop-blur-sm rounded-lg shadow-2xl p-8 md:p-12">
            <div className="prose prose-lg max-w-none">
              <p className="text-lg leading-relaxed text-gray-700 mb-6">
                The Crest Theater is a historic theater located in downtown Sacramento, California, just blocks from the State Capitol, the Sacramento Convention Center, and the new Sacramento Kings basketball arena (Golden 1 Center). It was originally opened in 1912 as the Empress Theatre, a vaudeville palace. It later operated as the Hippodrome Theatre until 1946. In 1949, the building was completely renovated and reopened as The Crest Theater.
              </p>

              <h3 className="text-2xl font-bold text-gray-800 mt-8 mb-4">A Legacy of Unforgettable Moments</h3>
              <p className="text-lg leading-relaxed text-gray-700 mb-6">
                For decades, The Crest Theater has stood as a cherished cultural landmark in the heart of Sacramento, weaving itself into the fabric of the city's vibrant arts scene. From its grand Art Deco beginnings to its present-day status as a premier entertainment venue, The Crest has consistently delivered exceptional experiences. Our historic stage has been graced by an extraordinary spectrum of talent, creating lasting memories for generations.
              </p>

              <h3 className="text-2xl font-bold text-gray-800 mt-8 mb-4">An Echo of Icons: Performers Who've Graced Our Stage</h3>
              <p className="text-lg leading-relaxed text-gray-700 mb-4">
                Nirvana, B.B. King, Etta James, No Doubt, Smashing Pumpkins, George Carlin, Tony Hinchcliff, X, Cab Calloway, John Hiatt, Cyndi Lauper, Dinosaur Jr., Guns N' Roses, Sonic Youth, Shawn Mendes, Pixies, Slayer, Los Lobos, Dwight Yoakam, Phish, Primus, Buddy Guy, Stanley Clarke, Lucinda Williams, Béla Fleck and the Flecktones, John Mayall, Carrot Top, Sam Kinison, Peter Murphy, Social Distortion, GWAR, Danzig, Exodus, Cheap Trick, BoDeans, Henry Rollins, 7 Seconds, Ryan Adams, Gillian Welch & David Rawlings, Bruce Cockburn, Jackie Greene, A.J. Croce, Cat Power, Billy Bragg, are just a few of the amazing acts that have graced our stage!
              </p>
            </div>
          </div>

          {/* Back to Home Button */}
          <div className="text-center mt-12">
            <button 
              onClick={() => window.scrollTo(0, 0)}
              className="bg-red-600 text-white px-8 py-3 rounded font-bold hover:bg-red-700 transition-colors"
            >
              ↑ Back to Top
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

// Gallery Component (separate page)
const GalleryPage = ({ images, theaterName }) => {
  const [selectedImage, setSelectedImage] = useState(null);
  const [selectedIndex, setSelectedIndex] = useState(0);
  
  // Find red theater curtain image from gallery
  const getRedCurtainImage = () => {
    const curtainImage = images.find(img => 
      img.includes('pexels-photo-713149') || 
      img.includes('red') || 
      img.toLowerCase().includes('curtain')
    );
    return curtainImage || "https://images.pexels.com/photos/713149/pexels-photo-713149.jpeg";
  };

  const backgroundImage = getRedCurtainImage();

  const openLightbox = (image, index) => {
    setSelectedImage(image);
    setSelectedIndex(index);
  };

  const closeLightbox = () => {
    setSelectedImage(null);
  };

  const nextImage = () => {
    const nextIndex = (selectedIndex + 1) % images.length;
    setSelectedImage(images[nextIndex]);
    setSelectedIndex(nextIndex);
  };

  const prevImage = () => {
    const prevIndex = selectedIndex === 0 ? images.length - 1 : selectedIndex - 1;
    setSelectedImage(images[prevIndex]);
    setSelectedIndex(prevIndex);
  };

  return (
    <section className="relative py-32 min-h-screen overflow-hidden">
      {/* Background Image */}
      <div 
        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage: `url(${backgroundImage})`,
          filter: 'grayscale(30%) brightness(0.2)',
          opacity: 0.7
        }}
      ></div>
      
      {/* Overlay */}
      <div className="absolute inset-0 bg-gradient-to-b from-red-900/80 to-black/90"></div>
      
      {/* Content */}
      <div className="relative z-10 container mx-auto px-4">
        <div className="text-center mb-12">
          <h1 className="text-5xl font-bold mb-4">
            <span className="text-red-600 italic font-script">The Crest</span>
            <span className="text-white"> THEATER</span>
          </h1>
          <h2 className="text-3xl font-bold text-white">GALLERY</h2>
          <p className="text-xl text-gray-300 mt-4">
            Explore the historic beauty and elegance of Sacramento's premier entertainment venue
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {images.map((image, index) => (
            <div key={index} className="group relative overflow-hidden rounded-lg shadow-2xl bg-white/10 backdrop-blur-sm cursor-pointer">
              <img
                src={image}
                alt={`Gallery ${index + 1}`}
                className="w-full h-64 object-cover group-hover:scale-110 transition-transform duration-500"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-50 transition-all duration-300 flex items-center justify-center">
                <button
                  onClick={() => openLightbox(image, index)}
                  className="text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-red-600 px-4 py-2 rounded font-semibold hover:bg-red-700"
                >
                  View Full Size
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* Theater History Section */}
        <div className="mt-16 bg-white/95 backdrop-blur-sm rounded-lg shadow-2xl p-8">
          <h3 className="text-3xl font-bold text-gray-800 mb-6 text-center">Historic Venue</h3>
          <div className="grid md:grid-cols-2 gap-8">
            <div>
              <h4 className="text-xl font-semibold mb-4">Architecture & Design</h4>
              <p className="text-gray-700 leading-relaxed">
                The Crest Theater showcases stunning period architecture with its ornate details, 
                elegant seating, and magnificent stage. Originally built as the Empress Theatre in 1912, 
                the venue has maintained its historic charm while incorporating modern amenities.
              </p>
            </div>
            <div>
              <h4 className="text-xl font-semibold mb-4">Performance Space</h4>
              <p className="text-gray-700 leading-relaxed">
                With excellent acoustics and intimate seating for audiences, The Crest Theater 
                provides an unparalleled live entertainment experience. The venue's design ensures 
                every seat offers great sightlines and connection to the performers.
              </p>
            </div>
          </div>
        </div>

        {/* Back to Home Button */}
        <div className="text-center mt-12">
          <button 
            onClick={() => window.scrollTo(0, 0)}
            className="bg-red-600 text-white px-8 py-3 rounded font-bold hover:bg-red-700 transition-colors"
          >
            ↑ Back to Top
          </button>
        </div>
      </div>

      {/* Lightbox Modal */}
      {selectedImage && (
        <div className="fixed inset-0 bg-black bg-opacity-90 z-50 flex items-center justify-center p-4">
          <div className="relative max-w-7xl max-h-full">
            <img
              src={selectedImage}
              alt="Full size view"
              className="max-w-full max-h-full object-contain"
            />
            
            {/* Close button */}
            <button
              onClick={closeLightbox}
              className="absolute top-4 right-4 bg-red-600 text-white rounded-full w-10 h-10 flex items-center justify-center hover:bg-red-700 transition-colors text-xl font-bold"
            >
              ×
            </button>
            
            {/* Previous button */}
            {images.length > 1 && (
              <button
                onClick={prevImage}
                className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-red-600 text-white rounded-full w-12 h-12 flex items-center justify-center hover:bg-red-700 transition-colors text-xl font-bold"
              >
                ‹
              </button>
            )}
            
            {/* Next button */}
            {images.length > 1 && (
              <button
                onClick={nextImage}
                className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-red-600 text-white rounded-full w-12 h-12 flex items-center justify-center hover:bg-red-700 transition-colors text-xl font-bold"
              >
                ›
              </button>
            )}
            
            {/* Image counter */}
            <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 bg-black bg-opacity-50 text-white px-4 py-2 rounded">
              {selectedIndex + 1} of {images.length}
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

// Footer Component
const Footer = ({ theaterName, contacts }) => {
  // Safe guard for contacts data during loading
  const safeContacts = contacts || {
    phone: '',
    email: '',
    address: {
      street: '',
      city: '',
      state: '',
      zip: ''
    },
    socialMedia: {
      facebook: '#',
      twitter: '#',
      tiktok: '#',
      instagram: '#',
      spotify: '#'
    }
  };
  return (
    <footer className="bg-black text-white py-12">
      <div className="container mx-auto px-4">
        <div className="grid md:grid-cols-4 gap-8">
          <div className="md:col-span-2">
            <h3 className="text-2xl font-bold mb-4">
              <span className="text-red-600 italic font-script">The Crest</span>
              <span className="text-yellow-600"> THEATER</span>
            </h3>
            <p className="text-gray-400 leading-relaxed">
              Historic theater in downtown Sacramento presenting live entertainment,
              movies, and special events. Experience the legend at Sacramento's premier entertainment destination.
            </p>
          </div>
          
          <div>
            <h4 className="text-lg font-semibold mb-4">Contact Info</h4>
            <p className="text-gray-400 mb-2">{safeContacts.address.street}</p>
            <p className="text-gray-400 mb-2">{safeContacts.address.city}, {safeContacts.address.state} {safeContacts.address.zip}</p>
            <p className="text-gray-400 mb-2">
              Phone: <a href={`tel:${safeContacts.phone}`} className="text-white hover:text-gray-300 transition-colors underline">{safeContacts.phone}</a>
            </p>
            <div className="text-gray-400 mb-2">
              <div>General Info:</div>
              <a href={`mailto:${safeContacts.generalInfoEmail || safeContacts.email}`} className="text-white hover:text-gray-300 transition-colors underline">{safeContacts.generalInfoEmail || safeContacts.email}</a>
            </div>
            {safeContacts.rentalBookingEmail && (
              <div className="text-gray-400 mb-2">
                <div>Rental/Booking:</div>
                <a href={`mailto:${safeContacts.rentalBookingEmail}`} className="text-white hover:text-gray-300 transition-colors underline">{safeContacts.rentalBookingEmail}</a>
              </div>
            )}
          </div>
          
          <div>
            <h4 className="text-lg font-semibold mb-4">Follow Us</h4>
            <div className="flex space-x-3">
              <a href={safeContacts.socialMedia.facebook} target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center hover:bg-blue-700 transition-colors" title="Facebook">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                </svg>
              </a>
              <a href={safeContacts.socialMedia.twitter} target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-black border border-gray-600 rounded-full flex items-center justify-center hover:bg-gray-800 transition-colors" title="X (Twitter)">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                </svg>
              </a>
              <a href={safeContacts.socialMedia.tiktok} target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-black border border-gray-600 rounded-full flex items-center justify-center hover:bg-gray-800 transition-colors" title="TikTok">
                <svg className="w-5 h-5" fill="white" viewBox="0 0 24 24">
                  <path d="M19.321 5.562a5.124 5.124 0 0 1-.443-.258 6.228 6.228 0 0 1-1.137-.966c-.849-.849-1.086-1.673-1.086-2.264V2h-3.006v14.63c0 2.259-1.832 4.091-4.091 4.091s-4.091-1.832-4.091-4.091 1.832-4.091 4.091-4.091c.446 0 .875.072 1.28.204v-3.13c-.424-.058-.857-.087-1.28-.087C4.775 9.526 0 14.301 0 20.185 0 26.069 4.775 30.844 10.659 30.844c5.884 0 10.659-4.775 10.659-10.659V9.697c1.007.6 2.188.952 3.435.952v-3.006c-1.932 0-3.623-.932-4.432-2.081z"/>
                </svg>
              </a>
              <a href={safeContacts.socialMedia.instagram} target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full flex items-center justify-center hover:from-purple-600 hover:to-pink-600 transition-colors" title="Instagram">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12.017 0C8.396 0 7.952.01 6.729.05 5.507.09 4.668.256 3.932.488 3.162.73 2.499 1.078 1.86 1.717.7 2.877.352 3.552.093 4.322c-.224.766-.39 1.605-.43 2.827C-.01 7.952 0 8.396 0 12.017c0 3.622.01 4.066.05 5.289.04 1.222.206 2.061.438 2.827.258.77.606 1.435 1.245 2.074.639.639 1.304.987 2.074 1.245.766.232 1.605.398 2.827.438 1.223.04 1.667.05 5.289.05 3.622 0 4.066-.01 5.289-.05 1.222-.04 2.061-.206 2.827-.438.77-.258 1.435-.606 2.074-1.245.639-.639.987-1.304 1.245-2.074.232-.766.398-1.605.438-2.827.04-1.223.05-1.667.05-5.289 0-3.622-.01-4.066-.05-5.289-.04-1.222-.206-2.061-.438-2.827-.258-.77-.606-1.435-1.245-2.074C20.491 1.078 19.826.73 19.056.488 18.29.256 17.451.09 16.229.05 15.006.01 14.562 0 10.941 0H12.017zM12.017 2.163c3.556 0 3.974.014 5.382.052 1.298.059 2.004.277 2.474.46.622.242 1.067.531 1.534.998.467.467.756.912.998 1.534.183.47.401 1.176.46 2.474.038 1.408.052 1.826.052 5.382 0 3.556-.014 3.974-.052 5.382-.059 1.298-.277 2.004-.46 2.474-.242.622-.531 1.067-.998 1.534-.467.467-.912.756-1.534.998-.47.183-1.176.401-2.474.46-1.408.038-1.826.052-5.382.052-3.556 0-3.974-.014-5.382-.052-1.298-.059-2.004-.277-2.474-.46-.622-.242-1.067-.531-1.534-.998-.467-.467-.756-.912-.998-1.534-.183-.47-.401-1.176-.46-2.474-.038-1.408-.052-1.826-.052-5.382 0-3.556.014-3.974.052-5.382.059-1.298.277-2.004.46-2.474.242-.622.531-1.067.998-1.534.467-.467.912-.756 1.534-.998.47-.183 1.176-.401 2.474-.46 1.408-.038 1.826-.052 5.382-.052z"/>
                  <circle cx="12.017" cy="12.017" r="3.708"/>
                  <circle cx="18.408" cy="5.622" r="1.178"/>
                </svg>
              </a>
              <a href={safeContacts.socialMedia.spotify} target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-green-500 rounded-full flex items-center justify-center hover:bg-green-600 transition-colors" title="Spotify">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0zm5.521 17.34c-.24.359-.66.48-1.021.24-2.82-1.74-6.36-2.101-10.561-1.141-.418.122-.779-.179-.899-.539-.12-.421.18-.78.54-.9 4.56-1.021 8.52-.6 11.64 1.32.42.18.479.659.301 1.02zm1.44-3.3c-.301.42-.841.6-1.262.3-3.239-1.98-8.159-2.58-11.939-1.38-.479.12-1.02-.12-1.14-.6-.12-.48.12-1.021.6-1.141C9.6 9.9 15 10.561 18.72 12.84c.361.181.54.78.241 1.2zm.12-3.36C15.24 8.4 8.82 8.16 5.16 9.301c-.6.179-1.2-.181-1.38-.721-.18-.601.18-1.2.72-1.381 4.26-1.26 11.28-1.02 15.721 1.621.539.3.719 1.02.42 1.56-.299.421-1.02.599-1.559.3z"/>
                </svg>
              </a>
            </div>
          </div>
        </div>
        
        <div className="border-t border-gray-800 mt-8 pt-8 text-center">
          <p className="text-gray-400">
            © 2025 {theaterName}. All rights reserved.
          </p>
        </div>
      </div>
    </footer>
  );
};

// Event Calendar Component
const EventCalendar = ({ events, theaterName, onEventClick }) => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth());
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());

  // Specific background image for calendar page
  const specificBackgroundImage = "https://i.imgur.com/VAer4Li.jpeg";

  // Auto-scroll to current date when component mounts
  useEffect(() => {
    const scrollToCurrentDate = () => {
      const today = new Date();
      const currentDay = today.getDate();
      const currentMonthElement = document.querySelector(`[data-day="${currentDay}"]`);
      
      if (currentMonthElement && selectedMonth === today.getMonth() && selectedYear === today.getFullYear()) {
        setTimeout(() => {
          currentMonthElement.scrollIntoView({ 
            behavior: 'smooth', 
            block: 'center' 
          });
        }, 500);
      }
    };

    scrollToCurrentDate();
  }, [selectedMonth, selectedYear]);

  const monthNames = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"
  ];

  const getDaysInMonth = (month, year) => {
    return new Date(year, month + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (month, year) => {
    return new Date(year, month, 1).getDay();
  };

  const formatEventDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      year: 'numeric'
    });
  };

  const formatEventTime = (timeString) => {
    return timeString || '7:00 PM';
  };

  const getEventsForDate = (day) => {
    const dateStr = `${selectedYear}-${String(selectedMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    return events.filter(event => {
      if (event.date) {
        const eventDate = parseCaliforniaDate(event.date);
        const checkDate = parseCaliforniaDate(dateStr);
        return eventDate && checkDate && eventDate.toDateString() === checkDate.toDateString();
      }
      return false;
    });
  };

  const getEventsForMonth = () => {
    return events.filter(event => {
      if (event.date) {
        const eventDate = parseCaliforniaDate(event.date);
        return eventDate && eventDate.getMonth() === selectedMonth && eventDate.getFullYear() === selectedYear;
      }
      return false;
    }).sort((a, b) => parseCaliforniaDate(a.date) - parseCaliforniaDate(b.date));
  };

  const renderCalendarDays = () => {
    const daysInMonth = getDaysInMonth(selectedMonth, selectedYear);
    const firstDay = getFirstDayOfMonth(selectedMonth, selectedYear);
    const days = [];

    // Empty cells for days before the first day of the month
    for (let i = 0; i < firstDay; i++) {
      days.push(<div key={`empty-${i}`} className="h-24 md:h-40 bg-gray-50"></div>);
    }

    // Days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      const dayEvents = getEventsForDate(day);
      const hasEvents = dayEvents.length > 0;
      
      // Check if this is today's date
      const today = new Date();
      const isToday = day === today.getDate() && 
                     selectedMonth === today.getMonth() && 
                     selectedYear === today.getFullYear();

      days.push(
        <div 
          key={day} 
          data-day={day}
          className={`h-24 md:h-40 border border-gray-200 p-1 md:p-3 ${
            hasEvents ? 'bg-blue-50' : 'bg-white'
          } ${isToday ? 'ring-4 ring-red-500 bg-red-50' : ''}`}
        >
          <div className={`font-semibold text-sm md:text-lg mb-1 ${
            isToday ? 'text-red-600 font-bold' : ''
          }`}>
            {day}
            {isToday && <span className="text-xs block text-red-600">TODAY</span>}
          </div>
          <div className="md:hidden">
            {dayEvents.slice(0, 2).map((event, idx) => (
              <button
                key={idx}
                onClick={() => onEventClick && onEventClick(event)}
                className={`block w-full text-xs text-white px-1 py-0.5 rounded mt-1 truncate transition-colors cursor-pointer ${getCalendarEventStyle(event.category)}`}
              >
                {event.title}
              </button>
            ))}
            {dayEvents.length > 2 && (
              <div className="text-xs text-gray-500">+{dayEvents.length - 2} more</div>
            )}
          </div>
          <div className="hidden md:block">
            {dayEvents.slice(0, 4).map((event, idx) => (
              <button
                key={idx}
                onClick={() => onEventClick && onEventClick(event)}
                className={`block w-full text-sm text-white px-2 py-1 rounded mt-1 truncate transition-colors cursor-pointer ${getCalendarEventStyle(event.category)}`}
              >
                {event.title}
              </button>
            ))}
            {dayEvents.length > 4 && (
              <div className="text-xs text-gray-500">+{dayEvents.length - 4} more</div>
            )}
          </div>
        </div>
      );
    }

    return days;
  };

  const changeMonth = (direction) => {
    if (direction === 'prev') {
      if (selectedMonth === 0) {
        setSelectedMonth(11);
        setSelectedYear(selectedYear - 1);
      } else {
        setSelectedMonth(selectedMonth - 1);
      }
    } else {
      if (selectedMonth === 11) {
        setSelectedMonth(0);
        setSelectedYear(selectedYear + 1);
      } else {
        setSelectedMonth(selectedMonth + 1);
      }
    }
  };

  const monthEvents = getEventsForMonth();

  return (
    <section className="relative py-32 min-h-screen overflow-hidden">
      {/* Background Image */}
      {specificBackgroundImage && (
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: `url(${specificBackgroundImage})`,
            filter: 'brightness(0.8)',
            opacity: 1
          }}
        ></div>
      )}
      
      {/* Overlay */}
      <div className="absolute inset-0 bg-gradient-to-b from-black/40 to-black/60"></div>
      
      {/* Content */}
      <div className="relative z-10 container mx-auto px-4">
        <div className="text-center mb-12">
          <h1 className="text-5xl font-bold mb-4" style={{ textShadow: '2px 2px 4px rgba(0,0,0,0.8)' }}>
            <span className="text-red-600 italic font-script">The Crest</span>
            <span className="text-white"> THEATER</span>
          </h1>
          <h2 className="text-3xl font-bold text-white" style={{ textShadow: '2px 2px 4px rgba(0,0,0,0.8)' }}>EVENT CALENDAR</h2>
        </div>

        <div className="max-w-7xl mx-auto">
          {/* Calendar Grid - Maximum Width */}
          <div className="bg-white/95 backdrop-blur-sm rounded-lg shadow-2xl p-4 md:p-8">
            <div className="flex justify-between items-center mb-6">
              <button 
                onClick={() => changeMonth('prev')}
                className="bg-red-600 text-white px-6 py-3 rounded hover:bg-red-700 transition-colors font-bold"
              >
                ← Prev
              </button>
              <h3 className="text-2xl md:text-3xl font-bold text-gray-800">
                {monthNames[selectedMonth]} {selectedYear}
              </h3>
              <button 
                onClick={() => changeMonth('next')}
                className="bg-red-600 text-white px-6 py-3 rounded hover:bg-red-700 transition-colors font-bold"
              >
                Next →
              </button>
            </div>

            {/* Calendar Header */}
            <div className="grid grid-cols-7 gap-1 md:gap-2 mb-2">
              {['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'].map((day, index) => (
                <div key={day} className="text-center font-semibold text-gray-600 py-3 md:py-4">
                  <span className="hidden md:block">{day}</span>
                  <span className="md:hidden">{['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][index]}</span>
                </div>
              ))}
            </div>

            {/* Calendar Days */}
            <div className="grid grid-cols-7 gap-1 md:gap-2">
              {renderCalendarDays()}
            </div>
          </div>
        </div>

        {/* Back to Home Button */}
        <div className="text-center mt-12">
          <button 
            onClick={() => window.scrollTo(0, 0)}
            className="bg-red-600 text-white px-8 py-3 rounded font-bold hover:bg-red-700 transition-colors"
          >
            ↑ Back to Top
          </button>
        </div>
      </div>
    </section>
  );
};

// FAQ Section Component
const FAQSection = ({ theaterName, setCurrentView, pageImages }) => {
  const faqBackgroundImage = pageImages?.faq || "https://i.imgur.com/CvzwGq0.jpeg";
  
  return (
    <section className="relative py-32 min-h-screen overflow-hidden">
      {/* Background Image */}
      <div 
        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage: `url(${faqBackgroundImage})`,
          filter: 'brightness(0.8)',
          opacity: 1
        }}
      ></div>
      
      {/* Overlay */}
      <div className="absolute inset-0 bg-gradient-to-b from-black/40 to-black/60"></div>
      
      {/* Content */}
      <div className="relative z-10 container mx-auto px-4">
        <div className="text-center mb-12">
          <h1 className="text-5xl font-bold mb-4" style={{ textShadow: '2px 2px 4px rgba(0,0,0,0.8)' }}>
            <span className="text-red-600 italic font-script">The Crest</span>
            <span className="text-white"> THEATER</span>
          </h1>
          <h2 className="text-3xl font-bold text-white" style={{ textShadow: '2px 2px 4px rgba(0,0,0,0.8)' }}>FAQ</h2>
          <p className="text-xl text-white mt-2" style={{ textShadow: '2px 2px 4px rgba(0,0,0,0.8)' }}>
            Frequently Asked Questions
          </p>
        </div>

        <div className="max-w-4xl mx-auto">
          <div className="bg-white/95 backdrop-blur-sm rounded-lg shadow-2xl p-8 md:p-12">
            <div className="prose prose-lg max-w-none">
              {/* General Guidelines */}
              <h3 className="text-2xl font-bold text-gray-800 mt-0 mb-6">General Guidelines</h3>
              <ul className="text-lg leading-relaxed text-gray-700 mb-8 space-y-2">
                <li><strong>WE DO NOT ALLOW WEAPONS OF ANY KIND!</strong></li>
                <li>All Patrons subject to wanding before entry to event.</li>
                <li>All bags are subject to search at the door.</li>
                <li>We do not allow you to bring professional camera equipment or audio recording devices unless approved by artist.</li>
                <li>We ask that you have your tickets ready when you arrive at the theater. If you need to purchase tickets, please make your way to the box office.</li>
                <li>Check out Empress Tavern for a drink or a bite to eat, or head to our concessions stand for theater staples such as popcorn, candy, and drinks.</li>
                <li>On busy nights, arriving at the theater 45 minutes before the show is set to begin is best practice, to ensure we can get you into the theater on time.</li>
                <li>We reserve the right to eject anyone who does not follow rules such as photo policy, fighting, public intoxication, or disturbance. We want you to have fun and we also need to make sure everyone's safety while inside the theater. We have security guards for all our shows.</li>
              </ul>
              <p className="text-lg text-gray-700 mb-8"><strong>Thank you!</strong></p>

              {/* Ticketing & Box Office */}
              <h3 className="text-2xl font-bold text-gray-800 mt-8 mb-4">Ticketing & Box Office</h3>
              <div className="text-lg leading-relaxed text-gray-700 mb-8 space-y-4">
                <p>
                  Please visit our{' '}
                  <button
                    onClick={() => {
                      setCurrentView('home');
                      setTimeout(() => {
                        const eventsSection = document.getElementById('upcoming-events');
                        if (eventsSection) {
                          const yOffset = -100;
                          const y = eventsSection.getBoundingClientRect().top + window.pageYOffset + yOffset;
                          window.scrollTo({ top: y, behavior: 'smooth' });
                        }
                      }, 100);
                    }}
                    className="text-red-600 hover:text-red-800 underline font-semibold"
                  >
                    EVENTS
                  </button>
                  {' '}tab to see our current schedule.
                </p>
                <p>Our Box Office is open Monday-Friday 12 PM-5 PM. It is also open at doors for our shows and movies.</p>
                <p>Tickets can be purchased anytime online. Our ticketer is Ticketmaster. We are not affiliated with any other third-party ticketer or ticket reseller. Be careful when buying suspect tickets on a different website than ours or Ticketmaster.</p>
              </div>

              {/* Contacting Us */}
              <h3 className="text-2xl font-bold text-gray-800 mt-8 mb-4">Contacting Us</h3>
              <div className="text-lg leading-relaxed text-gray-700 mb-8">
                <p className="mb-4">The best way to contact us is via email.</p>
                <p className="mb-2">
                  For general information please email:{' '}
                  <a href="mailto:info@cresttheater.com" className="text-red-600 hover:text-red-800 underline">
                    info@cresttheater.com
                  </a>
                </p>
                <p className="mb-4">
                  For Theater Rental and Booking please email:{' '}
                  <a href="mailto:booking@cresttheater.com" className="text-red-600 hover:text-red-800 underline">
                    booking@cresttheater.com
                  </a>
                </p>
                <p>We will be answering emails in the order they come in. We do our best to field questions in a timely manner.</p>
              </div>

              {/* Cancellations, Postponements, Refunds */}
              <h3 className="text-2xl font-bold text-gray-800 mt-8 mb-4">Cancellations, Postponements, Refunds</h3>
              <div className="text-lg leading-relaxed text-gray-700 mb-8 space-y-4">
                <p>If you are a ticket holder for a currently scheduled event and your event is cancelled or postponed, we will email you about your ticketing options. We do our best to respond in a timely manner, your patience when a show is cancelled or postponed is greatly appreciated.</p>
                <p>If you feel you have accidentally purchased too many tickets, need to switch seats, or have trouble getting seats together with your party feel free to contact us. We have a no refund policy, but we also will try to make every attempt to rectify any issues you may have with your tickets, including chargebacks and vouchers.</p>
                <p>
                  If you have questions email us at{' '}
                  <a href="mailto:info@cresttheater.com" className="text-red-600 hover:text-red-800 underline">
                    info@cresttheater.com
                  </a>
                </p>
              </div>

              {/* Theatre Rental Info */}
              <h3 className="text-2xl font-bold text-gray-800 mt-8 mb-4">Theater Rental/Booking Information</h3>
              <p className="text-lg leading-relaxed text-gray-700 mb-8">
                For rental or booking information, please email{' '}
                <a href="mailto:booking@cresttheater.com" className="text-red-600 hover:text-red-800 underline">
                  booking@cresttheater.com
                </a>
              </p>
            </div>
          </div>

          {/* Back to Home Button */}
          <div className="text-center mt-12">
            <button 
              onClick={() => window.scrollTo(0, 0)}
              className="bg-red-600 text-white px-8 py-3 rounded font-bold hover:bg-red-700 transition-colors"
            >
              ↑ Back to Top
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

// Empress Tavern Component
const EmpressTavern = ({ theaterName, setCurrentView, pageImages, empressSettings }) => {
  const backgroundImage = "https://i.imgur.com/QsQG6IA.jpeg";
  
  return (
    <section className="relative py-32 min-h-screen overflow-hidden">
      {/* Background Image */}
      <div 
        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage: `url(${backgroundImage})`,
          filter: 'brightness(0.8)',
          opacity: 1
        }}
      ></div>
      
      {/* Overlay */}
      <div className="absolute inset-0 bg-gradient-to-b from-black/40 to-black/60"></div>
      
      {/* Content */}
      <div className="relative z-10 container mx-auto px-4">
        <div className="text-center mb-12">
          <h1 className="text-5xl font-bold mb-4" style={{ textShadow: '2px 2px 4px rgba(0,0,0,0.8)' }}>
            <span className="text-red-600 italic font-script">The Crest</span>
            <span className="text-white"> THEATER</span>
          </h1>
          <h2 className="text-4xl font-bold text-yellow-400 mb-2" style={{ textShadow: '2px 2px 4px rgba(0,0,0,0.8)' }}>
            EMPRESS TAVERN
          </h2>
          <p className="text-xl text-white text-center" style={{ textShadow: '2px 2px 4px rgba(0,0,0,0.8)' }}>
            {empressSettings?.headerSubtext || 'Coming Soon - A Unique Sacramento Food Venue'}
          </p>
        </div>

        <div className="max-w-4xl mx-auto">
          <div className="bg-white/95 backdrop-blur-sm rounded-lg shadow-2xl p-8 md:p-12">
            <div className="prose prose-lg max-w-none text-center">
              
              {/* Main Announcement */}
              <div className="mb-8">
                <div 
                  className="text-xl leading-relaxed text-gray-800 mb-6 text-center"
                  dangerouslySetInnerHTML={{ 
                    __html: DOMPurify.sanitize((empressSettings?.partnershipDescription && empressSettings.partnershipDescription.trim()) || 
                    'The owners of Empress Tavern are partnering with <a href="https://alchemistcdc.org/" target="_blank" rel="noopener noreferrer" class="text-red-600 hover:text-red-800 underline">Alchemist CDC</a> to launch a truly unique Sacramento food venue.', {
                      ALLOWED_TAGS: ['p', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'div', 'span', 'a', 'strong', 'em', 'ul', 'ol', 'li', 'br'],
                      ALLOWED_ATTR: ['href', 'target', 'rel', 'class', 'style'],
                      ALLOWED_STYLES: {
                        '*': {
                          'color': [/^#[0-9A-F]{3,6}$/i, /^rgb\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})\s*\)$/i, /^rgba\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*[01]?\.?\d*\s*\)$/i],
                          'background-color': [/^#[0-9A-F]{3,6}$/i, /^rgb\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})\s*\)$/i],
                          'font-size': [/^(\d+)(px|em|rem|%|pt)$/i],
                          'font-weight': [/^(normal|bold|bolder|lighter|\d{3})$/i],
                          'font-family': [/.*/],
                          'text-decoration': [/^(none|underline|overline|line-through)$/i],
                          'font-style': [/^(normal|italic|oblique)$/i],
                          'text-align': [/^(left|right|center|justify)$/i]
                        }
                      }
                    })
                  }}
                />
                
                <div className="bg-red-100 border-l-4 border-red-500 p-6 mb-6">
                  <div 
                    className="text-center"
                    dangerouslySetInnerHTML={{ 
                      __html: DOMPurify.sanitize(empressSettings?.redBoxText || 
                      '<h3 class="text-2xl font-bold text-red-800 mb-2">Grand Opening</h3><p class="text-3xl font-bold text-red-600">July 2025</p>', {
                        ALLOWED_TAGS: ['p', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'div', 'span', 'a', 'strong', 'em', 'ul', 'ol', 'li', 'br'],
                        ALLOWED_ATTR: ['href', 'target', 'rel', 'class', 'style'],
                        ALLOWED_STYLES: {
                          '*': {
                            'color': [/^#[0-9A-F]{3,6}$/i, /^rgb\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})\s*\)$/i, /^rgba\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*[01]?\.?\d*\s*\)$/i],
                            'background-color': [/^#[0-9A-F]{3,6}$/i, /^rgb\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})\s*\)$/i],
                            'font-size': [/^(\d+)(px|em|rem|%|pt)$/i],
                            'font-weight': [/^(normal|bold|bolder|lighter|\d{3})$/i],
                            'font-family': [/.*/],
                            'text-decoration': [/^(none|underline|overline|line-through)$/i],
                            'font-style': [/^(normal|italic|oblique)$/i],
                            'text-align': [/^(left|right|center|justify)$/i]
                          }
                        }
                      })
                    }}
                  />
                </div>
              </div>

              {/* Featured Image */}
              <div className="mb-8">
                <img 
                  src={pageImages?.empress || "https://crestsacra1stg.wpenginepowered.com/wp-content/uploads/2020/03/photo-4-1024x768-1.jpg"}
                  alt="Empress Tavern Interior"
                  className="w-full h-64 md:h-80 object-cover rounded-lg shadow-lg"
                  loading="lazy"
                />
              </div>

              {/* Contact Information */}
              <div className="bg-gray-50 p-6 rounded-lg">
                <div 
                  className="text-center"
                  dangerouslySetInnerHTML={{ 
                    __html: DOMPurify.sanitize(empressSettings?.whiteBoxText || 
                    '<h3 class="text-2xl font-bold text-gray-800 mb-4">Media Inquiries & Booking Information</h3><div class="space-y-2"><p class="text-lg"><strong>Email:</strong> <a href="mailto:info@empresstavern.com" class="text-red-600 hover:text-red-800 underline">info@empresstavern.com</a></p><p class="text-lg"><strong>Partnership Inquiries:</strong> <a href="mailto:shannin@alchemistcdc.com" class="text-red-600 hover:text-red-800 underline">shannin@alchemistcdc.com</a></p></div>', {
                      ALLOWED_TAGS: ['p', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'div', 'span', 'a', 'strong', 'em', 'ul', 'ol', 'li', 'br'],
                      ALLOWED_ATTR: ['href', 'target', 'rel', 'class', 'style'],
                      ALLOWED_STYLES: {
                        '*': {
                          'color': [/^#[0-9A-F]{3,6}$/i, /^rgb\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})\s*\)$/i, /^rgba\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*[01]?\.?\d*\s*\)$/i],
                          'background-color': [/^#[0-9A-F]{3,6}$/i, /^rgb\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})\s*\)$/i],
                          'font-size': [/^(\d+)(px|em|rem|%|pt)$/i],
                          'font-weight': [/^(normal|bold|bolder|lighter|\d{3})$/i],
                          'font-family': [/.*/],
                          'text-decoration': [/^(none|underline|overline|line-through)$/i],
                          'font-style': [/^(normal|italic|oblique)$/i],
                          'text-align': [/^(left|right|center|justify)$/i]
                        }
                      }
                    })
                  }}
                />
              </div>

              {/* Additional Information */}
              <div className="mt-8 text-left">
                <div 
                  dangerouslySetInnerHTML={{ 
                    __html: DOMPurify.sanitize(empressSettings?.aboutPartnership || 
                    '<h3 class="text-2xl font-bold text-gray-800 mb-4">About This Partnership</h3><p class="text-lg leading-relaxed text-gray-700 mb-4">This exciting collaboration between Empress Tavern and Alchemist CDC represents a new chapter in Sacramento\'s dining and entertainment scene. Located within the historic Crest Theater, this venue will offer a unique culinary experience that complements the theater\'s rich cultural heritage.</p><p class="text-lg leading-relaxed text-gray-700 mb-4">Stay tuned for updates on menu offerings, special events, and opening details as we approach the grand opening in July 2025.</p>', {
                      ALLOWED_TAGS: ['p', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'div', 'span', 'a', 'strong', 'em', 'ul', 'ol', 'li', 'br'],
                      ALLOWED_ATTR: ['href', 'target', 'rel', 'class', 'style'],
                      ALLOWED_STYLES: {
                        '*': {
                          'color': [/^#[0-9A-F]{3,6}$/i, /^rgb\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})\s*\)$/i, /^rgba\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*[01]?\.?\d*\s*\)$/i],
                          'background-color': [/^#[0-9A-F]{3,6}$/i, /^rgb\(\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})\s*\)$/i],
                          'font-size': [/^(\d+)(px|em|rem|%|pt)$/i],
                          'font-weight': [/^(normal|bold|bolder|lighter|\d{3})$/i],
                          'font-family': [/.*/],
                          'text-decoration': [/^(none|underline|overline|line-through)$/i],
                          'font-style': [/^(normal|italic|oblique)$/i],
                          'text-align': [/^(left|right|center|justify)$/i]
                        }
                      }
                    })
                  }}
                />
              </div>

            </div>
          </div>

          {/* Back to Top Button - Centered */}
          <div className="text-center mt-12">
            <button 
              onClick={() => window.scrollTo(0, 0)}
              className="bg-red-600 text-white px-8 py-3 rounded font-bold hover:bg-red-700 transition-colors"
            >
              ↑ Back to Top
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

// Dynamic Star Banner Component - Mobile Responsive
const BannerWithDynamicStars = ({ bannerSettings }) => {
  const bannerRef = useRef(null);

  // Calculate fluid star size and font size using viewport units
  const starSize = Math.max(40, Math.min(80, window.innerWidth * 0.05)); // 5% of viewport width, clamped between 40-80px
  const baseFontSize = parseInt(bannerSettings.fontSize) || 24;
  const fluidFontSize = Math.max(14, Math.min(baseFontSize, window.innerWidth * 0.018)); // 1.8% of viewport width

  return (
    <div className="mt-8 w-full">
      {/* Constrain banner to same width as white text above - max-w-4xl with px-2 */}
      <div className="max-w-4xl mx-auto px-2">
        <div className="banner-container relative flex items-start justify-center w-full">
          {/* Left Star */}
          <div 
            className="star-left flex-shrink-0 mt-2"
            style={{
              width: `clamp(40px, 5vw, 80px)`,
              height: `clamp(40px, 5vw, 80px)`,
              backgroundColor: '#E9A319',
              clipPath: 'polygon(50% 0%, 61% 35%, 98% 35%, 68% 57%, 79% 91%, 50% 70%, 21% 91%, 32% 57%, 2% 35%, 39% 35%)',
              boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
              transition: 'all 0.3s ease',
              marginRight: 'clamp(4px, 1vw, 12px)'
            }}
          ></div>
          
          {/* Main banner rectangle - dynamic width and height with fluid scaling */}
          <div 
            ref={bannerRef}
            className="banner-main relative text-black flex-grow"
            style={{ 
              backgroundColor: '#E9A319',
              fontFamily: bannerSettings.fontFamily || 'Inter',
              fontSize: `clamp(14px, 1.8vw, ${baseFontSize}px)`,
              fontWeight: bannerSettings.isBold ? 'bold' : 'normal',
              fontStyle: bannerSettings.isItalic ? 'italic' : 'normal',
              textDecoration: bannerSettings.isUnderline ? 'underline' : 'none',
              minHeight: `clamp(40px, 5vw, 80px)`,
              maxHeight: 'none', // Allow unlimited height growth
              boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              wordWrap: 'break-word',
              overflowWrap: 'break-word',
              hyphens: 'auto',
              lineHeight: '1.4',
              textAlign: 'center',
              padding: 'clamp(8px, 1.5vw, 16px) clamp(12px, 2vw, 20px)'
            }}
          >
            <div 
              dangerouslySetInnerHTML={{ __html: bannerSettings.text || '' }}
              style={{ 
                width: '100%',
                textAlign: 'center'
              }}
            />
          </div>
          
          {/* Right Star */}
          <div 
            className="star-right flex-shrink-0 mt-2"
            style={{
              width: `clamp(40px, 5vw, 80px)`,
              height: `clamp(40px, 5vw, 80px)`,
              backgroundColor: '#E9A319',
              clipPath: 'polygon(50% 0%, 61% 35%, 98% 35%, 68% 57%, 79% 91%, 50% 70%, 21% 91%, 32% 57%, 2% 35%, 39% 35%)',
              boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
              transition: 'all 0.3s ease',
              marginLeft: 'clamp(4px, 1vw, 12px)'
            }}
          ></div>
        </div>
      </div>
    </div>
  );
};


// Export all components
const Components = {
  Header,
  HeroSection,
  NewsletterSignup,
  RentalInformation,
  FeaturedEvent,
  UpcomingEvents,
  AboutSection,
  GalleryPage,
  Footer,
  EventCalendar,
  EventDetail,
  FAQSection,
  EmpressTavern
};

export default Components;
